// ./src/components/features/PlayListFormModal/PlayListFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
} from "@mantine/core";
import { AppModal } from "@/components/generic/AppModal";
import { PlayList, PlayListInput } from "@/utils/api/PlayListService";
import { AppIcon } from "@/components/generic/AppIcon";
import { StatusSelector } from "@/components/features/StatusSelector";
import { BlockTransitionAnimationSelector } from "../../BlockTransitionAnimationSelector";
import { ProgressStyleSelector } from "../../ProgressStyleSelector";

export interface PlayListFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** PlayList to edit (if null/undefined, it's create mode) */
  playList?: PlayList | null;

  /** Callback when playlist should be created */
  onCreatePlayList?: (playListData: PlayListInput) => Promise<PlayList | null>;

  /** Callback when playlist should be updated */
  onUpdatePlayList?: (
    playListId: string,
    playListData: PlayListInput
  ) => Promise<PlayList | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from playlist operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface PlayListFormData {
  name: string | null | undefined;
  description: string | null | undefined;
  blockTransitionAnimation: string | null | undefined;
  progressStyle: string | null | undefined;
  status: number;
  duplicateOfId?: string | null | undefined;
}

const initialFormData: PlayListFormData = {
  name: "",
  description: "",
  blockTransitionAnimation: "flip",
  progressStyle: "bar",
  status: 100,
  duplicateOfId: undefined,
};

export const PlayListFormModal: React.FC<PlayListFormModalProps> = ({
  opened,
  onClose,
  playList,
  onCreatePlayList,
  onUpdatePlayList,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<PlayListFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof PlayListFormData, string>>
  >({});

  const isEditMode = Boolean(playList) && Boolean(playList?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or playlist changes
  useEffect(() => {
    if (opened) {
      if (playList) {
        // Edit mode - populate form with playlist data
        setFormData({
          name: playList.name,
          description: playList.description,
          blockTransitionAnimation: playList.blockTransitionAnimation,
          progressStyle: playList.progressStyle,
          status: playList.status != null ? playList.status : 100,
          duplicateOfId: playList.duplicateOfId,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, playList, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof PlayListFormData, string>> = {};

    // Required fields
    if (!formData.name?.trim()) {
      newErrors.name = "Playlist name is required";
    }

    setValidationErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof PlayListFormData,
    value: string | number | null | undefined
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "status" ? Number(value) : value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare playlist data
      const playListData: PlayListInput = {
        name: formData.name,
        description: formData.description,
        blockTransitionAnimation: formData.blockTransitionAnimation,
        progressStyle: formData.progressStyle,
        status: formData.status,
        duplicateOfId: formData?.duplicateOfId,
      };

      let result: PlayList | null = null;

      if (isEditMode && playList && onUpdatePlayList) {
        // Update existing playlist
        result = await onUpdatePlayList(playList.id || "", playListData);
      } else if (!isEditMode && onCreatePlayList) {
        // Create new playlist
        result = await onCreatePlayList(playListData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Playlist" : "Create New Playlist"}
      size="md"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Playlist Name"
              placeholder="Enter playlist name"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={validationErrors.name}
              required
              withAsterisk
              disabled={isSubmitting}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter playlist description"
              value={formData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={validationErrors.description}
              rows={3}
              disabled={isSubmitting}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Block Transition Animation */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Block Transition Animation</InputLabel>
              <BlockTransitionAnimationSelector
                value={formData.blockTransitionAnimation ?? null}
                onChange={(value) =>
                  handleInputChange("blockTransitionAnimation", value)
                }
                error={validationErrors.blockTransitionAnimation}
                required
                disabled={isSubmitting}
                size="md"
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Timer mode */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Timer Mode</InputLabel>
              <ProgressStyleSelector
                value={formData.progressStyle}
                onChange={(value) => handleInputChange("progressStyle", value)}
                error={validationErrors.progressStyle}
                required
                disabled={isSubmitting}
                size="md"
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Status */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={validationErrors.status}
                required
                disabled={isSubmitting}
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
